import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import logoAnimated from '@/assets/chopperswap-logo-no-bg.png';

interface AnimatedLogoLoaderProps {
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen';
  className?: string;
  showText?: boolean;
  text?: string;
  showProgress?: boolean;
}

const loadingMessages = [
  'Loading',
  'Preparing',
  'Almost ready'
];

const AnimatedLogoLoader: React.FC<AnimatedLogoLoaderProps> = ({
  size = 'md',
  className,
  showText = true,
  text,
  showProgress = false
}) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!showText || text) return;
    
    const messageInterval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 2000);

    return () => clearInterval(messageInterval);
  }, [showText, text]);

  useEffect(() => {
    if (!showProgress) return;
    
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) return prev;
        return prev + Math.random() * 15;
      });
    }, 300);

    return () => clearInterval(progressInterval);
  }, [showProgress]);

  const displayText = text || loadingMessages[messageIndex];

  // Size configurations - simplified
  const config = {
    sm: {
      container: 'w-24 h-24',
      logo: 'w-14 h-14',
      ring: 'w-20 h-20',
      text: 'text-xs'
    },
    md: {
      container: 'w-36 h-36',
      logo: 'w-20 h-20',
      ring: 'w-32 h-32',
      text: 'text-sm'
    },
    lg: {
      container: 'w-44 h-44',
      logo: 'w-24 h-24',
      ring: 'w-40 h-40',
      text: 'text-base'
    },
    xl: {
      container: 'w-52 h-52',
      logo: 'w-28 h-28',
      ring: 'w-44 h-44',
      text: 'text-lg'
    },
    fullscreen: {
      container: 'w-44 h-44',
      logo: 'w-24 h-24',
      ring: 'w-40 h-40',
      text: 'text-base'
    }
  };

  const currentConfig = config[size];

  return (
    <div className={cn(
      'flex flex-col items-center justify-center gap-6',
      className
    )}>
      {/* Main Container */}
      <div className={cn(
        'relative flex items-center justify-center',
        currentConfig.container
      )}>
        
        {/* Pulse Rings */}
        <div className={cn(
          'absolute rounded-full border-2 border-primary/25 animate-pulse-ring',
          currentConfig.ring
        )} />
        <div 
          className={cn(
            'absolute rounded-full border border-primary/15 animate-pulse-ring',
            currentConfig.ring
          )}
          style={{ animationDelay: '0.5s' }}
        />

        {/* Central Glow */}
        <div className={cn(
          'absolute rounded-full bg-primary/15 blur-xl animate-glow',
          currentConfig.logo
        )} />

        {/* Logo Container */}
        <div className={cn(
          'relative z-10 animate-floating',
          currentConfig.logo
        )}>
          {/* Logo Glow Effect */}
          <div className="absolute inset-0 animate-logo-glow">
            <img 
              src={logoAnimated} 
              alt="" 
              className="w-full h-full object-contain opacity-40 blur-sm dark:brightness-0 dark:invert"
            />
          </div>
          
          {/* Main Logo */}
          <img 
            src={logoAnimated} 
            alt="ChopperSwap" 
            className="relative w-full h-full object-contain drop-shadow-[0_0_12px_hsl(var(--primary)/0.4)] dark:brightness-0 dark:invert"
          />
        </div>
      </div>

      {/* Status Text */}
      {showText && (
        <div className="flex flex-col items-center gap-3">
          <div className={cn(
            'font-mono tracking-widest text-primary/85 uppercase',
            currentConfig.text
          )}>
            {displayText}
            <span className="animate-pulse">_</span>
          </div>

          {/* Progress Bar */}
          {showProgress && (
            <div className="w-48 h-1 bg-primary/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-primary/40 via-primary to-primary/40 transition-all duration-300 ease-out"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnimatedLogoLoader;
